import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2, ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { RelatedBlogPosts } from './RelatedBlogPosts';
import { BlogPostData } from '../../types/blog';
import { NavBar } from '../NavBar';
interface BlogPostTemplateProps {
  content: BlogPostData;
}
export const BlogPostTemplate = memo(function BlogPostTemplate({
  content
}: BlogPostTemplateProps) {
  const {
    articleData,
    seo,
    featuredImage,
    alternateUrls,
    intro,
    tableOfContents,
    sections,
    protectionSection,
    whyAiWorksSection,
    faqSchema,
    relatedPosts,
    translations = {}
  } = content;
  const t = {
    tableOfContents: translations.tableOfContents || 'Table of Contents',
    continueLearning: translations.continueLearning || 'Continue Learning',
    readyToSeeTruth: translations.readyToSeeTruth || '💀 Ready to See the Truth?',
    readyToSeeTruthDesc: translations.readyToSeeTruthDesc || 'Gaslighting is subtle, dangerous, and exhausting, but our AI can make it crystal clear. Upload your chats and get your instant toxicity report.',
    scanChatsNow: translations.scanChatsNow || 'Scan Your Chats Now →',
    shareArticle: translations.shareArticle || 'Share this article',
    backToBlog: translations.backToBlog || 'Back to Blog',
    protectionSectionTitle: translations.protectionSectionTitle || '🛡 How to Protect Yourself',
    whyAiWorksTitle: translations.whyAiWorksTitle || '💀 Why Our AI Works'
  };
  const handleShare = async () => {
    try {
      await navigator.share({
        title: articleData.title,
        text: articleData.description,
        url: window.location.href
      });
    } catch (err) {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };
  const canonicalUrl = seo.canonical || `https://toxicdetector.com/blog/${articleData.slug}`;
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://toxicdetector.com/'
    }, {
      '@type': 'ListItem',
      position: 2,
      name: 'Blog',
      item: 'https://toxicdetector.com/blog'
    }, {
      '@type': 'ListItem',
      position: 3,
      name: articleData.title,
      item: canonicalUrl
    }]
  };
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleData.title,
    description: articleData.description,
    image: featuredImage.url,
    datePublished: articleData.publishDate,
    dateModified: articleData.publishDate,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl
    },
    author: {
      '@type': 'Person',
      name: articleData.author,
      url: 'https://toxicdetector.com/founder',
      sameAs: ['https://www.linkedin.com/in/chukwuka-paulgreen-b64334385/']
    },
    publisher: {
      '@type': 'Organization',
      name: 'ToxicDetector AI & Forensic Systems LTD',
      url: 'https://toxicdetector.com/'
    }
  };
  const faqPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSchema.mainEntity.map(faq => ({
      '@type': 'Question',
      name: faq.name,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.acceptedAnswer.text
      }
    }))
  };
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': canonicalUrl,
    name: articleData.title,
    description: articleData.description,
    url: canonicalUrl,
    breadcrumb: breadcrumbSchema
  };
  return <>
      <Helmet data-id="element-792" data-mp="mvny0ycuvefj">
        <title data-id="element-793" data-mp="mv4oo619tyd4">{articleData.title}</title>
        <meta name="title" content={articleData.title} data-id="element-794" data-mp="mu34nc2a21j9" />
        <meta name="description" content={articleData.description} data-id="element-795" data-mp="hxmcm6ryj30w" />
        <meta name="keywords" content={seo.keywords} data-id="element-796" data-mp="2nl3lyayq3dv3" />
        <link rel="canonical" href={canonicalUrl} data-id="element-797" data-mp="2nmofbx9awqrd" />

        {alternateUrls?.map(alt => <link key={alt.lang} rel="alternate" hrefLang={alt.lang} href={alt.url} data-id="element-798" data-mp="2skzpk93id3a4" />)}

        <meta property="og:type" content="article" data-id="element-799" data-mp="31kmrqx42ad48" />
        <meta property="og:title" content={articleData.title} data-id="element-800" data-mp="31mfysdil6zcr" />
        <meta property="og:description" content={articleData.description} data-id="element-801" data-mp="36khfwswgcoll" />
        <meta property="og:url" content={canonicalUrl} data-id="element-802" data-mp="3bj2j91703oe9" />
        <meta property="og:image" content={seo.ogImage} data-id="element-803" data-mp="3bhhqnh48yfj1" />

        <meta name="twitter:card" content="summary_large_image" data-id="element-804" data-mp="3g9972ytov4vg" />
        <meta name="twitter:title" content={seo.twitterTitle || articleData.title} data-id="element-805" data-mp="3gc53gipw1lhs" />
        <meta name="twitter:description" content={seo.twitterDescription || articleData.description} data-id="element-806" data-mp="3pah5dgw7g5pj" />
        <meta name="twitter:image" content={seo.twitterImage} data-id="element-807" data-mp="3u8r23vxkved9" />

        <script type="application/ld+json" data-id="element-808" data-mp="8833f0qrg3pk">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json" data-id="element-809" data-mp="38zz5iilu1in">
          {JSON.stringify(faqPageSchema)}
        </script>
        <script type="application/ld+json" data-id="element-810" data-mp="3uf1dq11s3d91">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json" data-id="element-811" data-mp="3qdzevlbrx37l">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col w-full min-h-screen bg-gradient-to-b from-stone-50 to-white" data-id="element-812" data-mp="qyuatwr6tk1b">
        <NavBar data-id="element-813" data-mp="lz7wtl3jbzrg" />
        <header className="bg-white/90 border-b border-gray-200 sticky top-0 z-50 backdrop-blur-sm" data-id="element-814" data-mp="m0sql8i0zeo9">
          <div className="max-w-3xl mx-auto px-6 py-4" data-id="element-815" data-mp="h7gd9uisquat">
            <div className="flex items-center justify-between" data-id="element-816" data-mp="d7fnjdq8ywog">
              <a href="/" className="text-2xl font-bold tracking-tight text-gray-900" data-id="element-817" data-mp="d53c7jezvkkd">
                🚩 ToxicDetector
              </a>
              <button onClick={handleShare} className="p-2.5 hover:bg-gray-50 rounded-lg transition-colors" aria-label="Share article" data-id="element-818" data-mp="14wsjqmmqoo9f">
                <Share2 size={20} className="text-gray-600" data-id="element-819" data-mp="344it9u3ushqa" />
              </button>
            </div>
          </div>
        </header>

        <article className="max-w-3xl mx-auto w-full px-6 py-12" data-id="element-820" data-mp="2ufhda3xb60cb">
          <a href="/blog" className="inline-flex items-center text-gray-600 text-sm mb-10 hover:text-gray-900 transition-colors group" data-id="element-821" data-mp="2udo70pqlxi4u">
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-822" data-mp="2lfu9xcl0qkgm" />
            {t.backToBlog}
          </a>

          <header className="mb-12" data-id="element-823" data-mp="2bg3gle872bi4">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight text-gray-900" data-id="element-824" data-mp="2bh526cmu6ge3">
              {articleData.title}
            </h1>
            <div className="flex items-center text-gray-600 text-sm gap-3" data-id="element-825" data-mp="9ps0dwg60om9">
              <time dateTime={articleData.publishDate} data-id="element-826" data-mp="1u7aocfbz6iy7">
                {new Date(articleData.publishDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              </time>
              <span className="text-gray-300" data-id="element-827" data-mp="23z6zaqz5u3ju">•</span>
              <span data-id="element-828" data-mp="1z5chjt347yne">{articleData.author}</span>
            </div>
          </header>

          <div className="mb-12" data-id="element-829" data-mp="2cx94lipxficx">
            <img src={featuredImage.url} alt={featuredImage.alt} className="w-full rounded-2xl shadow-sm border border-gray-200" data-id="element-830" data-mp="2cw7hugzjt7fd" />
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 mb-12" data-id="element-831" data-mp="2mvg9323147y1">
            <h2 className="font-bold text-lg mb-4 text-gray-900" data-id="element-832" data-mp="2hxwx68tt0799">
              {t.tableOfContents}
            </h2>
            <ul className="space-y-3 text-sm" data-id="element-833" data-mp="sigxrl5olzwk">
              {tableOfContents.map(item => <li key={item.id} data-id="element-834" data-mp="nkf4k7ejkk7d">
                  <a href={`#${item.id}`} className="text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2" data-id="element-835" data-mp="nl6xtbko25uh">
                    <span className="text-gray-400" data-id="element-836" data-mp="6mf0mg6mleom">→</span>
                    {item.title}
                  </a>
                </li>)}
              <li data-id="element-837" data-mp="gdjetdoh8vie">
                <a href="#faq" className="text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2" data-id="element-838" data-mp="ge1l734usq41">
                  <span className="text-gray-400" data-id="element-839" data-mp="3jui5w7qc5k6d">→</span>
                  Frequently Asked Questions
                </a>
              </li>
            </ul>
          </div>

          <div className="prose prose-lg max-w-none" data-id="element-840" data-mp="3tt7mzyaer0lk">
            {intro.map((paragraph, idx) => <p key={idx} className={`mb-8 leading-relaxed ${idx === 0 ? 'text-xl text-gray-700' : 'text-lg text-gray-600'}`} dangerouslySetInnerHTML={{
            __html: paragraph
          }} data-id="element-841" data-mp="144fe0vv9iszo" data-mpi={idx} />)}

            {sections.map(section => <section key={section.id} id={section.id} className="mb-12" data-id="element-842" data-mp="2on94zccp1r1o">
                <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-843" data-mp="2juy3i52b1x70">
                  {section.title}
                </h2>
                {section.quote && <div className="bg-gray-50 border-l-4 border-purple-600 p-6 mb-6 rounded-r-xl" data-id="element-844" data-mp="29xblvatmfrjc">
                    <p className="italic text-gray-700 text-lg" data-id="element-845" data-mp="25vq1z5io6czn">
                      "{section.quote}"
                    </p>
                  </div>}
                {section.content.map((paragraph, idx) => <p key={idx} className="text-lg leading-relaxed text-gray-600 mb-4" dangerouslySetInnerHTML={{
              __html: paragraph
            }} data-id="element-846" data-mp="1vybzyaxhtqqv" data-mpi={idx} />)}
              </section>)}

            {protectionSection && <section className="mb-12" data-id="element-847" data-mp="2t7twar1s40up">
                <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-848" data-mp="2o7fn35ofykv3">
                  {t.protectionSectionTitle}
                </h2>
                <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-849" data-mp="324b8zypsk6sk">
                  <ul className="space-y-3 text-lg text-gray-600" data-id="element-850" data-mp="2y5bvhppzv1pi">
                    {protectionSection.tips.map((tip, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-851" data-mp="25g4zaxe0io7f" data-mpi={idx}>
                        <span className="text-purple-600 font-bold" data-id="element-852" data-mp="25hzjg9hgmcag">•</span>
                        <span dangerouslySetInnerHTML={{
                    __html: tip
                  }} data-id="element-853" data-mp="2j8ctqihtq32y" />
                      </li>)}
                  </ul>
                </div>
              </section>}

            {whyAiWorksSection && <section className="mb-12" data-id="element-854" data-mp="1mnu9o58dcmr9">
                <h2 className="text-3xl font-bold mb-6 tracking-tight text-gray-900" data-id="element-855" data-mp="1ckp0pkjmstis">
                  {t.whyAiWorksTitle}
                </h2>
                <p className="mb-6 text-lg leading-relaxed text-gray-600" data-id="element-856" data-mp="1hejey8ggwgah">
                  {whyAiWorksSection.intro}
                </p>
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-id="element-857" data-mp="17m3wzb567rpl">
                  <ul className="space-y-3 text-lg text-gray-600" data-id="element-858" data-mp="1vchkqpgq34ed">
                    {whyAiWorksSection.bullets.map((bullet, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-859" data-mp="209zwsoq1dzql" data-mpi={idx}>
                        <span className="text-purple-600 font-bold" data-id="element-860" data-mp="1letmhpjwrerg">•</span>
                        <span dangerouslySetInnerHTML={{
                    __html: bullet
                  }} data-id="element-861" data-mp="1qd4zua9ew7ef" />
                      </li>)}
                  </ul>
                </div>
                <p className="text-lg font-semibold text-gray-900" data-id="element-862" data-mp="evvdnfywf19f">
                  {whyAiWorksSection.outro}
                </p>
                <div className="mt-6" data-id="element-863" data-mp="2clsi7ahl0lic">
                  <Link to="/upload" className="inline-block bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors" data-id="element-864" data-mp="2cmv5z9jly7u1">
                    See how Toxic Detector analyzes patterns →
                  </Link>
                </div>
              </section>}

            <section id="faq" className="mb-12" data-id="element-865" data-mp="1tuj7wyzzlspp">
              <h2 className="text-3xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-866" data-mp="1ttrbjlykfqyd">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4" data-id="element-867" data-mp="1jxei8p6xnf45">
                {faqSchema.mainEntity.map((faq, index) => <details key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden group" data-id="element-868" data-mp="3jaikyr6q82am" data-mpi={index}>
                    <summary className="px-6 py-4 cursor-pointer font-semibold text-base text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-between" data-id="element-869" data-mp="3luewl4mtp9ib">
                      <span data-id="element-870" data-mp="3qrf3tivesib0">{faq.name}</span>
                      <ChevronDown size={20} className="text-gray-400 group-open:rotate-180 transition-transform" data-id="element-871" data-mp="3vjoskvksfcbp" />
                    </summary>
                    <div className="px-6 pb-4 text-gray-600 leading-relaxed text-sm" data-id="element-872" data-mp="32wm7p691b03x">
                      {faq.acceptedAnswer.text}
                    </div>
                  </details>)}
              </div>
            </section>

            <section className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-12" data-id="element-873" data-mp="3cw3anfwwizw7">
              <h3 className="text-xl font-bold mb-4 text-gray-900" data-id="element-874" data-mp="3hqqqkqdualz6">
                {t.continueLearning}
              </h3>
              <div className="space-y-3" data-id="element-875" data-mp="x2cf4c2o65x2">
                <a href="/blog/narcissism-and-gaslighting-explained" className="block p-4 bg-white rounded-lg hover:shadow-sm transition-shadow border border-gray-200" data-id="element-876" data-mp="x19nu31fq1gm">
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm" data-id="element-877" data-mp="qo5kf0gcqmxj">
                    Learn about narcissism & gaslighting →
                  </h4>
                  <p className="text-xs text-gray-600" data-id="element-878" data-mp="zmr5p3d7kg20">
                    Understand the psychology behind manipulation tactics
                  </p>
                </a>
                <a href="/blog/is-your-ex-covert-narcissist" className="block p-4 bg-white rounded-lg hover:shadow-sm transition-shadow border border-gray-200" data-id="element-879" data-mp="19l6rqrxdoz4n">
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm" data-id="element-880" data-mp="1id0a8jwgwjwv">
                    Is your ex a covert narcissist? →
                  </h4>
                  <p className="text-xs text-gray-600" data-id="element-881" data-mp="1xb13f65yohnb">
                    Discover hidden signs of covert narcissistic behavior
                  </p>
                </a>
                <Link to="/signup" className="block p-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors" data-id="element-882" data-mp="2bysz9vgb1ph0">
                  <h4 className="font-semibold mb-1 text-sm" data-id="element-883" data-mp="2203iy7424d2v">
                    Try the Toxic Detector for free →
                  </h4>
                  <p className="text-xs text-purple-100" data-id="element-884" data-mp="26wk6az3fmnew">
                    Upload your chats and get instant AI-powered analysis
                  </p>
                </Link>
              </div>
            </section>
          </div>

          <div className="bg-purple-600 rounded-2xl p-10 text-center text-white mt-16" data-id="element-885" data-mp="1j92itfr7abhq">
            <h2 className="text-3xl font-bold mb-4 tracking-tight" data-id="element-886" data-mp="1o7wzab1p3nyo">
              {t.readyToSeeTruth}
            </h2>
            <p className="text-purple-100 mb-8 text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-887" data-mp="19a4m3pjmfbnn">
              {t.readyToSeeTruthDesc}
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/20" data-id="element-888" data-mp="1e97u9iuwdxzu">
              <p className="font-bold text-xl" data-id="element-889" data-mp="v6fefwunw6qx">
                "🚩 83% toxic. Beware of manipulation!"
              </p>
            </div>
            <Link to="/signup" className="inline-block bg-white text-purple-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-50 transition-colors" data-id="element-890" data-mp="lf1ks5pe615p">
              {t.scanChatsNow}
            </Link>
          </div>

          <div style={{
          marginTop: '40px',
          paddingTop: '20px',
          borderTop: '1px solid #eee'
        }} data-id="element-891" data-mp="1e373kmwvxg2w">
            <h3 data-id="element-892" data-mp="144hmgwctbznp">Related Articles</h3>
            <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginTop: '10px'
          }} data-id="element-893" data-mp="147vm3wu3durg">
              <a href="/blog/is-your-ex-a-covert-narcissist" data-id="element-894" data-mp="3jwx1r0wexteu">
                Covert Narcissist Signs
              </a>
              <a href="/blog/passive-aggressive-texts-guide" data-id="element-895" data-mp="1zfegiprzmd6l">
                Passive Aggressive Texts
              </a>
              <a href="/blog/narcissism-gaslighting-explained" data-id="element-896" data-mp="24f0tqz7tftfe">
                Gaslighting Explained
              </a>
            </div>
          </div>

          <RelatedBlogPosts posts={relatedPosts} data-id="element-897" data-mp="29b91vsnlimd2" />

          <div className="mt-12 text-center" data-id="element-898" data-mp="2e4kd05zfxpas">
            <button onClick={handleShare} className="inline-flex items-center text-gray-600 hover:text-gray-900 font-medium transition-colors text-sm" data-id="element-899" data-mp="2e5llq3szhzpc">
              <Share2 size={18} className="mr-2" data-id="element-900" data-mp="2n6bh56ec1o2j" />
              {t.shareArticle}
            </button>
          </div>
        </article>

        <footer className="bg-white border-t border-gray-200 py-12 mt-16" data-id="element-901" data-mp="2x36bdd3kp2b0">
          <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-902" data-mp="xpitu39fe6uq">
            <p className="text-gray-500 text-sm" data-id="element-903" data-mp="xrddle8ynsx8">
              © {new Date().getFullYear()} ToxicDetector. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
});